 // hooks/useDocumentUploader.js
import { useState, useCallback } from "react";
import {
  MAX_DOCS,
  fileFingerprint,
  validateIncomingDocuments,
} from "../utils/uploadLimits";

const makeId = () => Math.random().toString(36).slice(2);

export default function useDocumentUploader({ toast } = {}) {
  const [documents, setDocuments] = useState([]); // [{id,name,type,size,uploadProgress}]
  const [docFiles, setDocFiles] = useState([]);   // File[]
  const [uploadErrors, setUploadErrors] = useState([]);
  const [isParsingDocs, setIsParsingDocs] = useState(false); // keep for UI disables if you want

  const totalBytes = docFiles.reduce((s, f) => s + (f?.size || 0), 0);

  const handleFiles = useCallback(
    async (fileList) => {
      setUploadErrors([]);
      const incoming = Array.from(fileList || []);
      if (!incoming.length) return;

      // slot limit
      const remainingSlots = Math.max(0, MAX_DOCS - documents.length);
      if (remainingSlots <= 0) {
        const msg = `You already attached ${MAX_DOCS} documents. Remove one to add another.`;
        setUploadErrors([msg]);
        toast?.({ title: "Upload limit reached", description: msg, variant: "destructive" });
        return;
      }

      const selected = incoming.slice(0, remainingSlots);

      const { acceptedFiles: validFiles, errors } = validateIncomingDocuments({
        existingFiles: docFiles,
        incomingFiles: selected,
      });

      if (errors.length) {
        setUploadErrors(errors);
        toast?.({ title: "Some files were not added", description: errors.join("\n"), variant: "destructive" });
        return;
      }

      const validFingerprints = new Set(validFiles.map(fileFingerprint));
      const validBrowserFiles = selected.filter((file) => validFingerprints.has(fileFingerprint(file)));

      const uiDocs = validFiles.map((file) => ({
        id: makeId(),
        name: file.name,
        type: file.type,
        size: file.size,
        uploadProgress: 0,
      }));

      setDocuments((prev) => [...prev, ...uiDocs]);
      setDocFiles((prev) => [...prev, ...validBrowserFiles]);

      toast?.({ title: "Files added", description: "We’ll summarize them on submit." });
    },
    [documents.length, docFiles, toast]
  );

  const removeDocument = useCallback((docId) => {
    setDocuments((prev) => {
      const idx = prev.findIndex((d) => d.id === docId);
      if (idx >= 0) setDocFiles((files) => files.filter((_, i) => i !== idx));
      return prev.filter((d) => d.id !== docId);
    });
  }, []);

  const clearAll = useCallback(() => {
    setDocuments([]);
    setDocFiles([]);
    setUploadErrors([]);
  }, []);



  
  const buildFormData = useCallback(
    (baseFields = {}, opts = {}) => {
      const { includeFiles = true } = opts;
  
      const fd = new FormData();
  
      // send JSON-ish fields as strings
      Object.entries(baseFields).forEach(([k, v]) => {
        if (v === undefined) return;
        if (Array.isArray(v) || (v && typeof v === "object")) fd.append(k, JSON.stringify(v));
        else fd.append(k, String(v));
      });
  
      // actual files (optional)
      if (includeFiles) {
        docFiles.forEach((f) => {
          if (f instanceof Blob) {
            fd.append("documents", f, f.name || "document");
          }
        });
      }
  
      return fd;
    },
    [docFiles]
  );
  

  return {
    documents,
    docFiles,
    uploadErrors,
    isParsingDocs,
    totalBytes,

    handleFiles,
    removeDocument,

    buildFormData,
    clearAll,
  };
}
